import React, { useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { ArrowRight, Check, KeyRound, X } from "lucide-react";
import { useAuth } from "../lib/auth";
import Brand from "../components/Brand";

const STRENGTH_RULES = [
  { key: "len", label: "At least 10 characters", test: (p) => p.length >= 10 },
  { key: "upper", label: "One uppercase letter", test: (p) => /[A-Z]/.test(p) },
  { key: "lower", label: "One lowercase letter", test: (p) => /[a-z]/.test(p) },
  { key: "digit", label: "One digit", test: (p) => /\d/.test(p) },
  { key: "special", label: "One special character (!@#$…)", test: (p) => /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?`~]/.test(p) },
];

function StrengthChecklist({ password, testIdPrefix = "reset-strength" }) {
  if (!password) return null;
  return (
    <ul style={{ listStyle: "none", padding: 0, margin: "8px 0 0", display: "grid", gridTemplateColumns: "1fr 1fr", gap: "4px 12px" }} data-testid={`${testIdPrefix}-list`}>
      {STRENGTH_RULES.map((r) => {
        const ok = r.test(password);
        return (
          <li key={r.key} style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 11.5, fontFamily: "'JetBrains Mono', monospace", color: ok ? "var(--pf-ok)" : "var(--pf-text-dim)" }} data-testid={`${testIdPrefix}-${r.key}`} data-ok={ok}>
            {ok ? <Check size={12} /> : <X size={12} />} {r.label}
          </li>
        );
      })}
    </ul>
  );
}

export default function ResetPassword() {
  const { resetPassword } = useAuth();
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const nav = useNavigate();

  const [pwd, setPwd] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [done, setDone] = useState(false);

  const strengthOk = useMemo(() => STRENGTH_RULES.every((r) => r.test(pwd)), [pwd]);
  const match = pwd && pwd === confirm;
  const formOk = token && strengthOk && match;

  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setBusy(true);
    const r = await resetPassword({ token, newPassword: pwd, confirmPassword: confirm });
    setBusy(false);
    if (r.ok) {
      setDone(true);
      // Send back to login after a beat
      setTimeout(() => nav("/login?verified=1", { replace: true }), 1200);
    } else {
      setErr(r.error || "Could not reset password.");
    }
  };

  return (
    <div className="pf-blueprint-bg" style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", padding: 24 }}>
      <div className="pf-card pf-grain" style={{ width: 460, padding: 32 }}>
        <Link to="/" className="pf-heading" style={{ display: "inline-flex", alignItems: "center", color: "var(--pf-text)", textDecoration: "none", marginBottom: 22 }}>
          <Brand height={84} withTagline testId="reset-brand" />
        </Link>

        {!token ? (
          <div data-testid="reset-no-token">
            <div className="pf-overline" style={{ marginBottom: 6 }}>ACCESS · RECOVERY</div>
            <h1 className="pf-heading" style={{ fontSize: 26, margin: "0 0 10px" }}>Missing reset token</h1>
            <p style={{ color: "var(--pf-text-muted)", fontSize: 13.5, lineHeight: 1.55, margin: "0 0 18px" }}>
              The reset link looks incomplete. Please request a new one.
            </p>
            <Link to="/forgot-password" className="btn btn-primary" data-testid="reset-request-new">
              Request a new link <ArrowRight size={13} />
            </Link>
          </div>
        ) : done ? (
          <div data-testid="reset-done">
            <div className="pf-overline" style={{ marginBottom: 6 }}>ACCESS · RECOVERY</div>
            <h1 className="pf-heading" style={{ fontSize: 26, margin: "0 0 10px" }}>Password updated</h1>
            <p style={{ color: "var(--pf-text-muted)", fontSize: 13.5, lineHeight: 1.55, margin: "0 0 18px" }}>
              You can now sign in with your new password.
            </p>
            <Link to="/login" className="btn btn-primary" data-testid="reset-go-login">
              Sign in <ArrowRight size={13} />
            </Link>
          </div>
        ) : (
          <>
            <div className="pf-overline" style={{ marginBottom: 6 }}>ACCESS · RECOVERY</div>
            <h1 className="pf-heading" style={{ fontSize: 28, margin: "0 0 10px" }}>Choose a new password</h1>
            <p style={{ color: "var(--pf-text-muted)", fontSize: 13.5, lineHeight: 1.55, margin: "0 0 20px" }}>
              Pick a strong password. The link is single-use and expires after 60 minutes.
            </p>

            <form onSubmit={submit}>
              <label className="pf-label" htmlFor="rp-pwd">NEW PASSWORD</label>
              <input id="rp-pwd" className="pf-input" type="password" autoComplete="new-password" required
                value={pwd} onChange={(e) => setPwd(e.target.value)} placeholder="••••••••" data-testid="reset-password" />
              <StrengthChecklist password={pwd} />

              <div style={{ height: 14 }} />

              <label className="pf-label" htmlFor="rp-confirm">CONFIRM NEW PASSWORD</label>
              <input id="rp-confirm" className="pf-input" type="password" autoComplete="new-password" required
                value={confirm} onChange={(e) => setConfirm(e.target.value)} placeholder="••••••••" data-testid="reset-confirm" />
              {confirm && !match && (
                <div style={{ marginTop: 6, fontSize: 11.5, color: "var(--pf-err)", fontFamily: "'JetBrains Mono', monospace" }} data-testid="reset-mismatch">
                  Passwords do not match.
                </div>
              )}

              {err && (
                <div className="pf-sev pf-sev-error" style={{ display: "block", marginTop: 14, padding: "8px 10px" }} data-testid="reset-error">
                  {err}
                </div>
              )}

              <button type="submit" className="btn btn-primary" style={{ marginTop: 20, width: "100%" }} disabled={busy || !formOk} data-testid="reset-submit">
                {busy ? <><span className="pf-spin" /> Updating…</> : <><KeyRound size={14} /> Update password <ArrowRight size={14} /></>}
              </button>
            </form>

            <div style={{ marginTop: 18, fontSize: 13, color: "var(--pf-text-muted)" }}>
              <Link to="/login" className="pf-link" data-testid="reset-cancel">Cancel</Link>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
